import { AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  queryFailureCopy,
  queryFailureKind,
  useQueryFailures,
  type QueryFailure,
} from "@/lib/queryFailure";

/**
 * The app's one honest answer to "this did not load".
 *
 * BEN-141 QF4. Before this, a 403 on a page's own data rendered nothing at all
 * and a 500 or an unreadable body rendered the page as though the data had
 * arrived — the shape a user acts on values that are not there. There is one
 * component rather than eighteen page-level branches because the failure is the
 * same failure everywhere and the sentence should be too. Pages that already
 * own a specific error state keep it: this is the floor, not the ceiling.
 */
export function QueryErrorNotice({
  error,
  area,
  onRetry,
  className,
}: {
  error: unknown;
  area?: string;
  onRetry?: () => void;
  className?: string;
}) {
  const kind = queryFailureKind(error);
  const { title, body, action } = queryFailureCopy(kind, area);

  return (
    <div
      role="alert"
      className={cn(
        "mb-4 flex gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
        <div className="mt-3">
          {action === "sign-in" ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/auth/login">Sign in again</Link>
            </Button>
          ) : (
            onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The shell binding: the same notice, driven by the query cache rather than by
 * a page.
 *
 * Mounted once in `AppLayout`, so every signed-in route reports a failed load
 * without a line of per-page work and without a new page being able to forget.
 * `useQueryFailures` narrows to observed queries that failed with no data to
 * show, which is exactly the set the user is currently reading nothing for.
 *
 * Only the first failure is rendered. When a page's whole payload fails, every
 * query on it fails together, and a column of five identical alerts describes
 * one outage five times.
 */
export function PageQueryErrorNotice({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const failures = useQueryFailures(queryClient);
  if (failures.length === 0) return null;

  const first: QueryFailure = failures[0];
  const area = failures.length > 1 ? "part of this page" : first.area;

  return (
    <QueryErrorNotice
      error={first.error}
      area={area}
      className={className}
      onRetry={() => {
        for (const failure of failures) {
          void queryClient.refetchQueries({ queryKey: failure.queryKey, exact: true });
        }
      }}
    />
  );
}
